import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDarkMode } from "../../components/Theme/useDarkMode";
import { fmtMoney, formatRelative } from "../OrgOverview/format";
import { addGoldenLoan, enqueueIncomeRun, fetchIncomeRuns } from "./api";
import GoldenDatasetPanel from "./GoldenDatasetPanel";
import "./IncomeCalculationRuns.css";

const ENV_KEY = "icr-env";
const TAB_KEY = "icr-tab";

const StatusPill = ({ status }) => (
  <span className={`icr-pill icr-pill--${status || "queued"}`}>
    {(status || "queued").replace("-", " ")}
  </span>
);

function QueueComposer({ env, onQueued }) {
  const [mode, setMode] = useState("existing-dev");
  const [devOrganizationId, setDevOrganizationId] = useState("");
  const [devLoanId, setDevLoanId] = useState("");
  const [prodOrganizationId, setProdOrganizationId] = useState("");
  const [prodLoanId, setProdLoanId] = useState("");
  const [targetDevOrganizationId, setTargetDevOrganizationId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);

  if (env !== "dev") {
    return null;
  }

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setSubmissionError(null);
    const payload =
      mode === "existing-dev"
        ? {
            mode,
            organizationId: devOrganizationId.trim(),
            loanId: devLoanId.trim(),
          }
        : {
            mode,
            prodOrganizationId: prodOrganizationId.trim(),
            prodLoanId: prodLoanId.trim(),
            devOrganizationId: targetDevOrganizationId.trim(),
          };

    try {
      const result = await enqueueIncomeRun(payload, env);
      if (!result.success) {
        throw new Error(
          result.reason || "Could not add this loan to the queue.",
        );
      }
      onQueued({
        id: result.jobId,
        loanId: result.loanId,
        devLoanId: result.devLoanId,
        prodLoanId: result.prodLoanId,
        organizationId: result.organizationId,
        borrowerName: result.borrowerName,
        loanType: result.loanType,
        status: result.status,
        progressStage: "queued",
        source: result.source,
        createdAt: Date.now(),
      });
      if (mode === "existing-dev") {
        setDevLoanId("");
      } else {
        setProdLoanId("");
      }
      toast.success(
        mode === "clone-prod"
          ? "Clone and income calculation added to the queue."
          : "Income calculation added to the queue.",
      );
    } catch (error) {
      setSubmissionError(error.message);
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="icr-queue-composer">
      <div className="icr-queue-composer__intro">
        <div>
          <h2>Add a loan to the queue</h2>
          <p>
            Submit a Dev loan or clone one from Prod. You can keep adding loans
            while earlier jobs run.
          </p>
        </div>
        <span className="icr-queue-composer__ready">Queue ready</span>
      </div>

      <div className="icr-mode-switch" role="tablist" aria-label="Loan source">
        <button
          type="button"
          role="tab"
          aria-selected={mode === "existing-dev"}
          className={mode === "existing-dev" ? "is-active" : ""}
          onClick={() => setMode("existing-dev")}
        >
          Existing Dev loan
          <small>Run a loan already in Dev</small>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "clone-prod"}
          className={mode === "clone-prod" ? "is-active" : ""}
          onClick={() => setMode("clone-prod")}
        >
          Clone from Prod
          <small>Copy to Dev, then run</small>
        </button>
      </div>

      <form className="icr-queue-form" onSubmit={submit}>
        {mode === "existing-dev" ? (
          <>
            <label>
              Dev Organization ID
              <input
                required
                value={devOrganizationId}
                onChange={(event) => setDevOrganizationId(event.target.value)}
                placeholder="Organization ID"
              />
            </label>
            <label>
              Dev Loan ID
              <input
                required
                value={devLoanId}
                onChange={(event) => setDevLoanId(event.target.value)}
                placeholder="Loan ID"
              />
            </label>
          </>
        ) : (
          <>
            <label>
              Prod Organization ID
              <input
                required
                value={prodOrganizationId}
                onChange={(event) => setProdOrganizationId(event.target.value)}
                placeholder="Source organization"
              />
            </label>
            <label>
              Prod Loan ID
              <input
                required
                value={prodLoanId}
                onChange={(event) => setProdLoanId(event.target.value)}
                placeholder="Source loan"
              />
            </label>
            <label>
              Target Dev Organization ID
              <input
                required
                value={targetDevOrganizationId}
                onChange={(event) =>
                  setTargetDevOrganizationId(event.target.value)
                }
                placeholder="Destination organization"
              />
            </label>
          </>
        )}

        <button
          className="icr-btn icr-btn--primary icr-queue-form__submit"
          disabled={submitting}
          type="submit"
        >
          {submitting
            ? "Adding…"
            : mode === "clone-prod"
              ? "Clone and add to queue"
              : "Add to queue"}
        </button>
      </form>

      {mode === "clone-prod" && (
        <p className="icr-queue-composer__note">
          The original Prod loan is never changed. Cloning and calculation both
          happen asynchronously in Dev.
        </p>
      )}
      {submissionError && (
        <div className="icr-queue-composer__error">{submissionError}</div>
      )}
    </section>
  );
}

export default function IncomeCalculationRunsPage({ onReady }) {
  const [theme] = useDarkMode();
  const history = useHistory();

  const [env, setEnv] = useState(() => localStorage.getItem(ENV_KEY) || "dev");
  const [tab, setTab] = useState(
    () => localStorage.getItem(TAB_KEY) || "runs",
  );
  const [orgFilter, setOrgFilter] = useState("");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingGoldenId, setAddingGoldenId] = useState(null);

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      try {
        const res = await fetchIncomeRuns(
          { organizationId: orgFilter.trim() || undefined, limit: 100 },
          env,
        );
        if (res.success) {
          setJobs(Array.isArray(res.jobs) ? res.jobs : []);
        } else {
          setError(res.reason || "Failed to load income runs.");
        }
      } catch (err) {
        setError(err.message);
        toast.error(err.message);
      } finally {
        if (!silent) {
          setLoading(false);
        }
        onReady?.();
      }
    },
    [env, onReady, orgFilter],
  );

  useEffect(() => {
    if (tab !== "runs") {
      onReady?.();

      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [env, tab]);

  useEffect(() => {
    if (tab !== "runs") {
      return undefined;
    }
    const hasActiveJobs = jobs.some((job) =>
      ["queued", "running"].includes(job.status),
    );
    if (!hasActiveJobs) {
      return undefined;
    }
    const timer = window.setInterval(() => load({ silent: true }), 10000);

    return () => window.clearInterval(timer);
  }, [jobs, load, tab]);

  const onEnvChange = (next) => {
    localStorage.setItem(ENV_KEY, next);
    setEnv(next);
  };

  const onTabChange = (next) => {
    localStorage.setItem(TAB_KEY, next);
    setTab(next);
  };

  const openRun = (jobId) => {
    history.push(`/income-calculation-runs/${jobId}?env=${env}`);
  };

  const addQueuedJob = (job) => {
    setJobs((current) => [
      job,
      ...current.filter((existing) => existing.id !== job.id),
    ]);
  };

  const promoteToGolden = async (job, event) => {
    event.stopPropagation();
    if (env !== "dev") {
      toast.error("Golden dataset can only be edited in Dev.");

      return;
    }
    const loanId = job.devLoanId || job.loanId;
    if (!job.organizationId || !loanId) {
      toast.error("This run does not have a Dev loan to add yet.");

      return;
    }
    setAddingGoldenId(job.id);
    try {
      const result = await addGoldenLoan(
        {
          organizationId: job.organizationId,
          loanId,
          sourceJobId: job.id,
        },
        env,
      );
      if (!result.success) {
        throw new Error(result.reason || "Could not add to golden dataset.");
      }
      toast.success(
        result.created
          ? "Added to golden dataset."
          : "Already in the golden dataset.",
      );
      onTabChange("golden");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAddingGoldenId(null);
    }
  };

  return (
    <div className="icr-page" data-theme={theme}>
      <ToastContainer position="bottom-right" theme={theme} />
      <div className="icr-header">
        <div>
          <h1 className="icr-title">Income Runs</h1>
          <p className="icr-subtitle">
            Queue and review income calculations, or manage the golden dataset
            and compare version history.
          </p>
        </div>
        <div className="icr-controls">
          <select
            className="icr-select"
            value={env}
            onChange={(e) => onEnvChange(e.target.value)}
          >
            <option value="dev">Dev</option>
            <option value="prod">Prod</option>
          </select>
          {tab === "runs" && (
            <>
              <input
                className="icr-input"
                placeholder="Filter by Organization ID"
                value={orgFilter}
                onChange={(e) => setOrgFilter(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                style={{ minWidth: 220 }}
              />
              <button className="icr-btn" onClick={load} disabled={loading}>
                {loading ? "Loading…" : "Refresh"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="icr-page-tabs" role="tablist" aria-label="Income Runs sections">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "runs"}
          className={tab === "runs" ? "is-active" : ""}
          onClick={() => onTabChange("runs")}
        >
          Queue & runs
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "golden"}
          className={tab === "golden" ? "is-active" : ""}
          onClick={() => onTabChange("golden")}
        >
          Golden dataset
        </button>
      </div>

      {tab === "golden" ? (
        <GoldenDatasetPanel env={env} />
      ) : (
        <>
          <QueueComposer env={env} onQueued={addQueuedJob} />

          {error && <div className="icr-error">{error}</div>}

          {!error && (
            <div className="icr-table-scroll">
              <table className="icr-table">
                <thead>
                  <tr>
                    <th>Borrower(s)</th>
                    <th>Loan type</th>
                    <th>Dev Loan ID</th>
                    <th>Prod Loan ID</th>
                    <th>Status</th>
                    <th className="icr-num">Available / mo</th>
                    <th className="icr-num">Qualifying / mo</th>
                    <th>Organization</th>
                    <th>Source</th>
                    <th>Created</th>
                    <th>Golden</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job) => (
                    <tr
                      key={job.id}
                      className="icr-row"
                      onClick={() => openRun(job.id)}
                    >
                      <td>
                        <span className="icr-borrower-name">
                          {job.borrowerName || "Borrower unavailable"}
                        </span>
                        {job.borrowerCount != null && (
                          <span className="icr-cell-note">
                            {job.borrowerCount}{" "}
                            {job.borrowerCount === 1 ? "borrower" : "borrowers"}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="icr-loan-type">
                          {job.loanType || "Not provided"}
                        </span>
                      </td>
                      <td className="icr-mono">
                        {job.devLoanId ||
                          (job.source !== "prod-clone"
                            ? job.loanId
                            : "Pending clone")}
                      </td>
                      <td className="icr-mono">
                        {job.prodLoanId ||
                          job.cloneSource?.sourceLoanId ||
                          job.cloneRequest?.sourceLoanId ||
                          "—"}
                      </td>
                      <td>
                        <StatusPill status={job.status} />
                        {job.progressStage &&
                          job.progressStage !== job.status && (
                            <span className="icr-stage">
                              {job.progressStage.replace("-", " ")}
                            </span>
                          )}
                      </td>
                      <td className="icr-num">
                        {typeof job.availableMonthlyIncome === "number"
                          ? fmtMoney(job.availableMonthlyIncome)
                          : "—"}
                      </td>
                      <td className="icr-num">
                        {typeof job.qualifyingMonthlyIncome === "number"
                          ? fmtMoney(job.qualifyingMonthlyIncome)
                          : "—"}
                      </td>
                      <td className="icr-mono">{job.organizationId}</td>
                      <td>
                        {job.source === "prod-clone"
                          ? "Prod clone"
                          : job.source === "admin"
                            ? "Admin"
                            : job.source === "golden"
                              ? "Golden"
                              : job.source || "—"}
                      </td>
                      <td>{formatRelative(job.createdAt)}</td>
                      <td onClick={(event) => event.stopPropagation()}>
                        <button
                          type="button"
                          className="icr-btn"
                          disabled={
                            env !== "dev" ||
                            addingGoldenId === job.id ||
                            !(job.devLoanId || job.loanId)
                          }
                          onClick={(event) => promoteToGolden(job, event)}
                        >
                          {addingGoldenId === job.id ? "Adding…" : "Add"}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!loading && jobs.length === 0 && (
                    <tr>
                      <td colSpan={11} className="icr-muted">
                        No income runs found for this{" "}
                        {orgFilter ? "org" : "environment"}.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
